/**
 * ActivityFields — shared form fields for creating/editing an emission activity.
 *
 * Used by both DetailsStep (single activity flow) and AssetActivitiesStep (asset modal).
 * Renders: sub-type selectors, period, quantity, unit, region, data quality.
 * Consumers wrap this in their own Card / Modal and add any extra fields they need.
 */

import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  ActivityUnit,
  FuelType,
  VehicleType,
  RefrigerantGasType,
  FugitiveGasType,
  ProcessType,
  Scope1ActivityType,
  EmissionScope,
  getValidUnits,
  getStationaryFuels,
  getMobileFuels,
  UNIT_CATEGORIES,
  type UnitCategory,
  type ActivityType,
  type ActivityTypeWithUnits,
} from '../../types/emissions';
import {
  inputClass,
  selectClass,
  needsFuelType,
  needsVehicleType,
  needsRefrigerantType,
  needsProcessType,
  REGION_OPTIONS,
  VEHICLE_TYPE_CARDS,
  FUEL_TYPE_CARDS,
  REFRIGERANT_TYPE_CARDS,
  PROCESS_TYPE_CARDS,
  UNIT_LABELS,
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
} from './types';
import { TypeCardSelector } from './TypeCardSelector';
import { MonthPicker } from './MonthPicker';

// ─── Value interface ───────────────────────────────────────────────────────

export interface ActivityFieldValues {
  scope: EmissionScope | '';
  activityType: ActivityType | '';
  periodStart: string;
  periodEnd: string;
  quantity: string;
  unit: ActivityUnit | '';
  fuelType: FuelType | '';
  vehicleType: VehicleType | '';
  refrigerantType: RefrigerantGasType | FugitiveGasType | '';
  processType: ProcessType | '';
  region: string;
  regionFallback: boolean;
  yearFallback: boolean;
  name: string;
  dataSource: string;
  notes: string;
}

export interface ActivityFieldsProps {
  values: ActivityFieldValues;
  onUpdate: (updates: Partial<ActivityFieldValues>) => void;
  children?: React.ReactNode;
  /** Whether the Additional Details section starts open (default: false) */
  defaultDetailsOpen?: boolean;
  /** Field keys that are pre-set and should be shown as read-only badges */
  lockedFields?: Set<string>;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const toOptions = <T extends string>(values: T[], labels: Record<string, string>) =>
  values.map((v) => ({ value: v, label: labels[v] ?? v }));

// ─── Component ─────────────────────────────────────────────────────────────

export const ActivityFields = ({ values, onUpdate, children, defaultDetailsOpen = false, lockedFields }: ActivityFieldsProps) => {
  const isLocked = (field: string) => lockedFields?.has(field) ?? false;
  const [detailsOpen, setDetailsOpen] = useState(defaultDetailsOpen);
  // Derived options
  const validUnits = useMemo(
    () => getValidUnits(values.activityType as ActivityTypeWithUnits, values.fuelType as FuelType || undefined),
    [values.activityType, values.fuelType]
  );
  const unitOptions = toOptions(validUnits, UNIT_LABELS);

  const fuelTypeOptions = useMemo(() => {
    if (values.activityType === Scope1ActivityType.MobileCombustion) {
      return toOptions(getMobileFuels(values.vehicleType as VehicleType || undefined, values.scope as EmissionScope), FUEL_TYPE_LABELS);
    }
    return toOptions(getStationaryFuels(), FUEL_TYPE_LABELS);
  }, [values.activityType, values.vehicleType, values.scope]);

  const vehicleTypeOptions = useMemo(
    () => toOptions(Object.values(VehicleType), VEHICLE_TYPE_LABELS),
    []
  );

  const refrigerantTypeOptions = useMemo(
    () => toOptions([...Object.values(RefrigerantGasType), ...Object.values(FugitiveGasType)], GAS_TYPE_LABELS),
    []
  );

  const processTypeOptions = useMemo(
    () => toOptions(Object.values(ProcessType), PROCESS_TYPE_LABELS),
    []
  );

  // Unit filtering
  const [unitFilter, setUnitFilter] = useState<UnitCategory | null>(null);

  const unitCategories = useMemo(() => {
    const cats = new Set<UnitCategory>();
    for (const opt of unitOptions) {
      const cat = UNIT_CATEGORIES[opt.value as ActivityUnit];
      if (cat) cats.add(cat);
    }
    return [...cats];
  }, [unitOptions]);

  const filteredUnitOptions = useMemo(() => {
    if (!unitFilter) return unitOptions;
    return unitOptions.filter((opt) => UNIT_CATEGORIES[opt.value as ActivityUnit] === unitFilter);
  }, [unitOptions, unitFilter]);

  const showUnitFilter = unitCategories.length > 1;

  return (
    <div className='tw:space-y-5'>
      {/* Vehicle Type (mobile combustion) */}
      {needsVehicleType(values.activityType) && (
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Vehicle Type</label>
          {isLocked('vehicleType') ? (
            <Badge variant='secondary' className='tw:text-sm tw:py-1 tw:px-3'>
              {vehicleTypeOptions.find((o) => o.value === values.vehicleType)?.label ?? values.vehicleType}
            </Badge>
          ) : (
            <TypeCardSelector
              options={VEHICLE_TYPE_CARDS.filter((c) => vehicleTypeOptions.some((o) => o.value === c.value))}
              value={values.vehicleType}
              onChange={(v) => onUpdate({ vehicleType: v as VehicleType })}
            />
          )}
        </div>
      )}

      {/* Fuel Type (stationary and mobile combustion) — required for both. */}
      {needsFuelType(values.activityType) && (
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Fuel Type</label>
          {isLocked('fuelType') ? (
            <Badge variant='secondary' className='tw:text-sm tw:py-1 tw:px-3'>
              {fuelTypeOptions.find((o) => o.value === values.fuelType)?.label ?? values.fuelType}
            </Badge>
          ) : (
            <TypeCardSelector
              options={FUEL_TYPE_CARDS.filter((c) => fuelTypeOptions.some((o) => o.value === c.value))}
              value={values.fuelType}
              onChange={(v) => onUpdate({ fuelType: v as FuelType, unit: '' as ActivityUnit })}
            />
          )}
        </div>
      )}

      {/* Gas / Refrigerant Type (fugitive emissions) */}
      {needsRefrigerantType(values.activityType) && (
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Gas / Refrigerant Type</label>
          {isLocked('refrigerantType') ? (
            <Badge variant='secondary' className='tw:text-sm tw:py-1 tw:px-3'>
              {refrigerantTypeOptions.find((o) => o.value === values.refrigerantType)?.label ?? values.refrigerantType}
            </Badge>
          ) : (
            <TypeCardSelector
              options={REFRIGERANT_TYPE_CARDS.filter((c) => refrigerantTypeOptions.some((o) => o.value === c.value))}
              value={values.refrigerantType}
              onChange={(v) => onUpdate({ refrigerantType: v as RefrigerantGasType | FugitiveGasType })}
            />
          )}
        </div>
      )}

      {/* Process Type (process emissions) */}
      {needsProcessType(values.activityType) && (
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Process Type</label>
          {isLocked('processType') ? (
            <Badge variant='secondary' className='tw:text-sm tw:py-1 tw:px-3'>
              {processTypeOptions.find((o) => o.value === values.processType)?.label ?? values.processType}
            </Badge>
          ) : (
            <TypeCardSelector
              options={PROCESS_TYPE_CARDS.filter((c) => processTypeOptions.some((o) => o.value === c.value))}
              value={values.processType}
              onChange={(v) => onUpdate({ processType: v as ProcessType })}
            />
          )}
        </div>
      )}

      {/* Period — see MonthPicker for the popover UX. Cross-field rules:
        *   - When the user picks a start and end is empty, default end to
        *     the same month (single-month input is the common case).
        *   - When the user picks a start that's after the current end,
        *     snap end forward to match (end >= start invariant).
        *   - The end picker disables months before the current start. */}
      <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Period Start</label>
          <MonthPicker
            value={values.periodStart}
            onChange={(next) => {
              const updates: Partial<ActivityFieldValues> = { periodStart: next };
              if (!values.periodEnd) {
                updates.periodEnd = next;
              } else if (next > values.periodEnd) {
                // Lexicographic compare works on YYYY-MM strings.
                updates.periodEnd = next;
              }
              onUpdate(updates);
            }}
          />
        </div>
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Period End</label>
          <MonthPicker
            value={values.periodEnd}
            min={values.periodStart || undefined}
            onChange={(next) => onUpdate({ periodEnd: next })}
          />
        </div>
      </div>

      {/* Quantity and Unit */}
      <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
        <div>
          <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Quantity</label>
          <input
            type='number'
            placeholder='e.g., 1000'
            value={values.quantity}
            onChange={(e) => onUpdate({ quantity: e.target.value })}
            min='0'
            step='0.01'
            className={inputClass}
          />
          {values.quantity !== '' && Number(values.quantity) === 0 && (
            <p className='tw:text-xs tw:text-amber-600 tw:mt-1'>
              A quantity of 0 will result in zero emissions. Are you sure?
            </p>
          )}
        </div>
        <div className='tw:md:col-span-2'>
          <label className='tw:text-sm tw:font-medium tw:mb-1.5'>Unit</label>
          {showUnitFilter && (
            <span className='tw:font-normal tw:text-xs tw:ml-1.5'>
              (
              <Button
                variant='link'
                size='xs'
                onClick={() => setUnitFilter(null)}
                className={`tw:px-1 ${unitFilter === null ? 'tw:text-emerald-700 tw:font-medium' : 'tw:text-muted-foreground'}`}
              >
                All
              </Button>
              <span className='tw:text-muted-foreground'>{' | '}</span>
              {unitCategories.map((cat, i) => (
                <span key={cat}>
                  {i > 0 && <span className='tw:text-muted-foreground'>{' | '}</span>}
                  <Button
                    variant='link'
                    size='xs'
                    onClick={() => setUnitFilter(cat)}
                    className={`tw:px-1 ${unitFilter === cat ? 'tw:text-emerald-700 tw:font-medium' : 'tw:text-muted-foreground'}`}
                  >
                    {cat}
                  </Button>
                </span>
              ))}
              )
            </span>
          )}
          <div className='tw:flex tw:flex-wrap tw:gap-1.5'>
            {filteredUnitOptions.map((option) => (
              <button
                key={option.value}
                type='button'
                onClick={() => onUpdate({ unit: option.value as ActivityUnit })}
                className={cn(
                  'tw:px-3 tw:py-1 tw:rounded-full tw:border tw:text-xs tw:font-medium tw:transition-all tw:cursor-pointer',
                  values.unit === option.value
                    ? 'tw:border-emerald-500 tw:bg-emerald-50 tw:text-emerald-700 tw:shadow-sm'
                    : 'tw:border-input tw:hover:border-emerald-300 tw:hover:bg-muted/50',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Collapsible: Additional Details (Data Quality, Region, Fallback, Name, Data Source, Notes) */}
      <div className='tw:border-t tw:border-border tw:pt-4'>
        <Button
          variant='link'
          size='xs'
          className='tw:p-0'
          onClick={() => setDetailsOpen(!detailsOpen)}
        >
          {detailsOpen
            ? <ChevronDown className='tw:size-4 tw:text-muted-foreground' />
            : <ChevronRight className='tw:size-4 tw:text-muted-foreground' />
          }
          <span className='tw:text-sm tw:font-medium'>Additional Details</span>
        </Button>

        {/* Collapsed summary */}
        {!detailsOpen && (
          <div className='tw:mt-2 tw:ml-6 tw:flex tw:flex-wrap tw:gap-x-4 tw:gap-y-1 tw:text-xs tw:text-muted-foreground'>
            <span>
              <span className='tw:font-medium'>Region:</span>{' '}
              {REGION_OPTIONS.find((r) => r.value === values.region)?.label ?? (values.region || 'Not set')}
            </span>
            <span>
              <span className='tw:font-medium'>Fallback:</span>{' '}
              {[values.regionFallback && 'Region', values.yearFallback && 'Year'].filter(Boolean).join(', ') || 'None'}
            </span>
            <span><span className='tw:font-medium'>Name:</span> {values.name || '-'}</span>
            <span><span className='tw:font-medium'>Source:</span> {values.dataSource || '-'}</span>
            <span><span className='tw:font-medium'>Notes:</span> {values.notes.length > 60 ? `${values.notes.slice(0, 60)}…` : values.notes || '-'}</span>
          </div>
        )}

        {detailsOpen && (
          <div className='tw:mt-4 tw:space-y-5'>
            {/* Factor Region and Fallback */}
            <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>
                  Factor Region <span className='tw:text-destructive'>*</span>
                </label>
                <select
                  value={values.region}
                  onChange={(e) => onUpdate({ region: e.target.value })}
                  className={cn(selectClass, 'tw:max-w-xs')}
                  required
                >
                  <option value=''>Select region...</option>
                  {REGION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                  Region affects emission factors used in calculations
                </div>
              </div>
              <div className='tw:md:col-span-2'>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Factor Fallback</label>
                <div className='tw:flex tw:flex-wrap tw:gap-1.5'>
                  {(
                    [
                      { field: 'regionFallback' as const, label: 'Region', description: 'Use an alternative region factor if exact region unavailable' },
                      { field: 'yearFallback' as const, label: 'Year', description: 'Use the nearest available year if exact year has no factor' },
                    ] as const
                  ).map((option) => (
                    <button
                      key={option.field}
                      type='button'
                      title={option.description}
                      onClick={() => onUpdate({ [option.field]: !values[option.field] })}
                      className={cn(
                        'tw:px-3 tw:py-1 tw:rounded-full tw:border tw:text-xs tw:font-medium tw:transition-all tw:cursor-pointer',
                        values[option.field]
                          ? 'tw:border-emerald-500 tw:bg-emerald-50 tw:text-emerald-700 tw:shadow-sm'
                          : 'tw:border-input tw:hover:border-emerald-300 tw:hover:bg-muted/50',
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className='tw:text-xs tw:text-muted-foreground tw:mt-1'>
                  Enabled by default. Allows calculations to succeed when an exact factor match is unavailable.
                </div>
              </div>
            </div>

            {/* Name, Data Source, Notes */}
            <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4'>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Name / Label</label>
                <input
                  type='text'
                  placeholder='e.g. London office fleet, HQ gas boiler'
                  value={values.name}
                  onChange={(e) => onUpdate({ name: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Data Source</label>
                <input
                  type='text'
                  placeholder='e.g., Utility invoice, Fuel receipts'
                  value={values.dataSource}
                  onChange={(e) => onUpdate({ dataSource: e.target.value })}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Notes</label>
              <textarea
                rows={3}
                placeholder='Any additional details about this activity...'
                value={values.notes}
                onChange={(e) => onUpdate({ notes: e.target.value })}
                className='tw:flex tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-2 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring tw:resize-none'
              />
            </div>
          </div>
        )}
      </div>

      {/* Consumer-provided extra fields (fallback toggles, etc.) */}
      {children}
    </div>
  );
};
